import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useAnimatedCounter } from './useAnimatedCounter';
import { Activity, Layers, TrendingUp, Globe } from 'lucide-react';

const metrics = [
  { 
    target: '2,800+', 
    label: 'Vaults Scored', 
    sub: 'Real-Time DeFi Data',
    icon: Activity,
    accent: '#00D4FF',
  },
  { 
    target: '5', 
    label: 'Risk Dimensions', 
    sub: 'Scoring Framework',
    icon: Layers,
    accent: '#0066FF',
  },
  { 
    target: '4', 
    label: 'Strategy Adapters', 
    sub: 'Aave · Compound · UniV3 · Aero',
    icon: TrendingUp,
    accent: '#00FFD0',
  },
  {
    target: '4',
    label: 'Chains Monitored',
    sub: 'Base · ETH · Arb · BNB',
    icon: Globe,
    accent: '#00D4FF',
  },
];

function MetricNode({ metric, index, isInView }) {
  const counter = useAnimatedCounter(metric.target, 2000);
  const Icon = metric.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.08 + index * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="group relative"
      data-testid={`metric-${index}`}
    >
      <div className="relative overflow-hidden rounded-xl border border-rax-border/30 bg-rax-card/40 backdrop-blur-sm transition-all duration-300 group-hover:border-rax-cyan/20 group-hover:bg-rax-card/60">
        {/* Top accent line */}
        <div 
          className="absolute top-0 left-0 right-0 h-px opacity-30 group-hover:opacity-60 transition-opacity"
          style={{ background: `linear-gradient(90deg, transparent, ${metric.accent}, transparent)` }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center text-center px-4 py-5">
          {/* Icon */}
          <div
            className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-300"
            style={{ 
              backgroundColor: `${metric.accent}10`,
              color: metric.accent,
            }}
          >
            <Icon size={16} strokeWidth={2} />
          </div>

          {/* Value */}
          <span 
            ref={counter.ref}
            className="font-heading text-2xl font-bold text-rax-heading tracking-tight"
          >
            {counter.value}
          </span>

          {/* Label */}
          <span className="mt-0.5 font-body text-xs font-medium text-rax-body-light">
            {metric.label}
          </span>

          {/* Subtitle */}
          <span className="mt-0.5 font-mono text-[9px] tracking-wider text-rax-body/40 uppercase">
            {metric.sub}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export default function StatsBanner() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-40px' });

  return (
    <section ref={sectionRef} data-testid="stats-banner" className="relative py-6">
      {/* Section divider line */}
      <div className="absolute top-0 left-[15%] right-[15%] h-px bg-gradient-to-r from-transparent via-rax-border/50 to-transparent" />
      
      {/* Section header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.4 }}
        className="mx-auto max-w-7xl px-6 mb-5"
      >
        <div className="flex items-center justify-center gap-3">
          <div className="h-px w-8 bg-gradient-to-r from-transparent to-rax-cyan/30" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-rax-cyan/50">
            Protocol Metrics
          </span>
          <div className="h-px w-8 bg-gradient-to-l from-transparent to-rax-cyan/30" />
        </div>
      </motion.div>

      <div className="mx-auto max-w-4xl px-6">
        {/* Metrics grid */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {metrics.map((metric, i) => (
            <MetricNode key={metric.label} metric={metric} index={i} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
}
